import React, { useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { pickNativeTaskPhoto, pickNativeTaskPhotos } from '../utils/taskPhotoFile';
import { MAX_TASK_PHOTOS, MAX_TASK_VENDORS } from '../shared/projectTaskLimits';
import { openVendorOptions } from '../shared/projectTaskVendor';
import { formatPhoneDisplay } from '../shared/phoneFormat';

/**
 * Vendors and photos on one project task.
 * The same block is used for the agent, the homeowner who received the
 * project, and a homeowner who created the project.
 */
function ProjectTaskMedia({
  colors,
  brandColor,
  vendors,
  photos,
  vendorOptions,
  canEditVendors,
  canAddPhoto,
  onAddVendor,
  onRemoveVendor,
  onPhotoFile,
  onRemovePhoto,
  photoBusy,
}) {
  const inputRef = useRef(null);
  const [picking, setPicking] = useState(false);
  const list = Array.isArray(vendors) ? vendors : [];
  const shots = Array.isArray(photos) ? photos : [];
  const atVendorLimit = list.length >= MAX_TASK_VENDORS;
  const atPhotoLimit = shots.length >= MAX_TASK_PHOTOS;
  const showVendors = list.length > 0 || canEditVendors;
  const showPhotos = shots.length > 0 || canAddPhoto;
  const vendorChoices = openVendorOptions(vendorOptions, list);
  const native = Capacitor.isNativePlatform();
  if (!showVendors && !showPhotos) return null;

  const room = Math.max(0, MAX_TASK_PHOTOS - shots.length);

  const deliverPhotos = (files) => {
    const picked = (Array.isArray(files) ? files : [files]).filter(Boolean).slice(0, room);
    if (picked.length && onPhotoFile) onPhotoFile(picked);
  };

  const choosePhoto = async () => {
    if (!canAddPhoto || photoBusy || picking || room < 1) return;
    setPicking(true);
    try {
      const nativeFiles = await pickNativeTaskPhotos(room);
      if (nativeFiles) {
        deliverPhotos(nativeFiles);
        return;
      }
      if (inputRef.current) inputRef.current.click();
    } catch (error) {
      if (onPhotoFile) onPhotoFile(null, error);
    } finally {
      setPicking(false);
    }
  };

  const takePhoto = async () => {
    if (!canAddPhoto || photoBusy || picking || room < 1) return;
    setPicking(true);
    try {
      const file = await pickNativeTaskPhoto({ source: 'camera' });
      if (file) deliverPhotos([file]);
    } catch (error) {
      if (onPhotoFile) onPhotoFile(null, error);
    } finally {
      setPicking(false);
    }
  };

  return (
    <div style={{ marginTop: '0.75rem' }}>
      {showVendors && (
        <div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary, fontWeight: 650, marginBottom: '0.4rem' }}>
            Vendors{canEditVendors ? ` (up to ${MAX_TASK_VENDORS})` : ''}
          </div>
          {list.map((vendor) => (
            <div
              key={`${vendor.source || 'agent'}:${vendor.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.35rem',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: colors.text, fontWeight: 650 }}>{vendor.name || 'Vendor'}</div>
                {vendor.phone ? (
                  <a href={`tel:${vendor.phone}`} style={{ color: brandColor, textDecoration: 'none' }}>
                    {formatPhoneDisplay(vendor.phone) || vendor.phone}
                  </a>
                ) : null}
              </div>
              {canEditVendors && (
                <button
                  type="button"
                  aria-label={`Remove ${vendor.name || 'Vendor'}`}
                  onClick={() => onRemoveVendor(vendor)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: colors.textSecondary,
                    cursor: 'pointer',
                    minHeight: '44px',
                    minWidth: '44px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {canEditVendors && !atVendorLimit && vendorChoices.length > 0 && (
            <select
              aria-label="Add a vendor"
              value=""
              onChange={(event) => {
                if (event.target.value) onAddVendor(event.target.value);
              }}
              style={{
                width: '100%',
                maxWidth: '100%',
                minHeight: '44px',
                marginTop: '0.25rem',
                background: colors.surface,
                color: colors.text,
                border: `2px solid ${colors.border}`,
                borderRadius: '0.5rem',
              }}
            >
              <option value="">Add a vendor</option>
              {vendorChoices.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          )}
          {canEditVendors && atVendorLimit && (
            <p style={{ margin: '0.35rem 0 0', color: colors.textSecondary, fontSize: '0.85rem' }}>
              This task has 3 vendors.
            </p>
          )}
        </div>
      )}

      {showPhotos && (
        <div style={{ marginTop: showVendors ? '0.75rem' : 0 }}>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary, fontWeight: 650, marginBottom: '0.4rem' }}>
            Photos
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {shots.map((photo) => (
              <div key={photo.id} style={{ position: 'relative' }}>
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt=""
                    style={{
                      width: '96px',
                      height: '96px',
                      objectFit: 'cover',
                      borderRadius: '0.5rem',
                      border: `1px solid ${colors.border}`,
                      display: 'block',
                      background: colors.surfaceAlt,
                    }}
                  />
                ) : null}
                {onRemovePhoto && photo.removable !== false && (
                  <button
                    type="button"
                    aria-label="Remove photo"
                    onClick={() => onRemovePhoto(photo)}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      minWidth: '28px',
                      minHeight: '28px',
                      borderRadius: '999px',
                      border: 'none',
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          {canAddPhoto && !atPhotoLimit && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {native && (
                  <button
                    type="button"
                    onClick={takePhoto}
                    disabled={photoBusy || picking}
                    style={{
                      marginTop: '0.5rem',
                      minHeight: '44px',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.75rem',
                      border: `2px solid ${brandColor}`,
                      background: colors.surface,
                      color: brandColor,
                      fontWeight: 650,
                      cursor: 'pointer',
                    }}
                  >
                    {photoBusy || picking ? 'Adding photo…' : 'Take photo'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={choosePhoto}
                  disabled={photoBusy || picking}
                  style={{
                    marginTop: '0.5rem',
                    minHeight: '44px',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.75rem',
                    border: `2px solid ${brandColor}`,
                    background: colors.surface,
                    color: brandColor,
                    fontWeight: 650,
                    cursor: 'pointer',
                  }}
                >
                  {photoBusy || picking ? 'Adding photo…' : (native ? 'Choose photos' : 'Add photo')}
                </button>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                aria-label="Choose task photos"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const files = event.target.files ? Array.from(event.target.files) : [];
                  event.target.value = '';
                  if (files.length) deliverPhotos(files);
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectTaskMedia;
